// React
import { useEffect, useState } from "react";

// MUI
import { GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { Fade, SxProps } from "@mui/material";

import { generateSx } from "@/Layouts/components/Tables/DataSmartTable/helpers/";

// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";

// Helpers
import { tableKeys } from "@/Constants/tableKeys";

// Types
import { Inventory } from "@DataPages/types/inventory/models/inventory.ts";
import { GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid-pro";
import { VehicleCategory } from "@DataPages/types/inventory/commons";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { LoadingSpinner } from "@/Layouts/components/LoadingSpinner";
import { InventoryTab } from "../constants";
import { useInventoryTableColumns } from "../hooks/useInventoryTableColumns";
import { usePersistentSelection } from "@/Common/hooks/selection/usePersistentSelection";

const AUTO_GENERATED_ID = "auto-generated-group-footer-root";

type VehicleInformationTableProps = {
	rows: Inventory[];
	vehicleCategory: VehicleCategory;
	onShowDetailedInformation: (
		from: "isVehicleDetailedInformationDialogOpen" | "isGPSInformationDialogOpen",
		action: "open" | "close",
		vin?: string
	) => void;
	onShowMultipleGPSVehicleDetails: (selectedRows: GridRowSelectionModel) => void;
	apiRef?: React.MutableRefObject<GridApiPremium>;
	hasNextPage: boolean;
	inventoryTab: InventoryTab;
};

export default function VehicleInformationTable(props: Readonly<VehicleInformationTableProps>) {
	const [sx, setSx] = useState<SxProps>({});
	const apiRef = props.apiRef;

	const columns = useInventoryTableColumns({
		tab: props.inventoryTab,
		onShowDetailedInformation: props.onShowDetailedInformation,
		rows: props.rows,
	});

	const { selectedIds } = usePersistentSelection({
		table: props.inventoryTab,
	});

	useEffect(() => {
		if (apiRef) {
			setSx(generateSx(apiRef, { yGap: "0px" }));
		}
	}, [apiRef]);

	return (
		<DataSmartTable
			filterStoreConfig={{
				key: tableKeys.DATA_PAGES.DEALER_INVENTORY,
			}}
			apiRef={apiRef}
			rows={props.rows}
			columns={columns}
			hideFooter
			maxRows={21}
			enableCustomScrollbars={true}
			disableColumnMenu
			checkboxSelection
			disableRowSelectionOnClick
			columnVisibilityModel={{ _id: false }}
			rowSelectionModel={selectedIds}
			initialState={{
				pinnedColumns: {
					left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, "DMS", "GPS", "KMS", "stockId", "vin", "empty"],
				},
				aggregation: {
					model: {
						stockId: "size",
					},
				},
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					customComponent: (
						<Fade in={props.hasNextPage} timeout={400}>
							<div>
								<LoadingSpinner label="Inventory data is still loading..." className="items-center" />
							</div>
						</Fade>
					),
					filter: {
						disabled: false,
					},
					search: {
						useQuickFilter: true,
					},
				},
				noRowsOverlay: {
					title: "No Inventory Available",
					className: "!pb-[5px]",
				},
			}}
			sx={{
				"& .MuiDataGrid-pinnedColumnHeader--left .MuiDataGrid-pinnedColumnHeader--right": {
					border: "none",
				},
				"& .MuiDataGrid-columnHeader": {
					border: "none !important",
				},
				"& .MuiDataGrid-filler--pinnedLeft": {
					display: "none !important",
				},
				"& .MuiDataGrid-scrollbarContent": {
					width: "2200px !important",
					marginTop: -0.5,
				},
				...sx,
			}}
			getRowClassName={(params) => {
				if (params.id === AUTO_GENERATED_ID) {
					return "group/footer table-footer";
				}
				return params.indexRelativeToCurrentPage % 2 === 1 ? "group/stripe even-row" : "group/stripe odd-row";
			}}
			customProps={{
				tableClassName: {
					"display-select-all-checkbox": true,
					"[&_*]:!border-none pt-5": true,
					"[&_.MuiDataGrid-main]:overflow-visible": true,
					"[&_.MuiDataGrid-row:hover_.MuiDataGrid-cell:not(.footer):not(.empty-row)]:!shadow-[inset_0_0_0_100px_#E8E8E8]": true,
					"[&_.MuiDataGrid-row:hover_.MuiDataGrid-cell:not(.footer):not(.empty-row):not(.empty-row)]:shadow-[inset_0_0_0_100px_#E8E8E8]":
						true,
					"[&_.MuiDataGrid-row:hover_.MuiDataGrid-cell--pinnedLeft_.pinned]:!shadow-[inset_0_0_0_100px_#E8E8E8]": true,
				},
				expandableRows: false,
				hide: false,
			}}
		/>
	);
}
